import * as React from "react"
import { Command as CommandPrimitive } from "cmdk"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
import { Tag } from "@/components/ui/tag"

export interface Option {
  id: string
  name: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Max number of selections allowed (e.g. from question validation.maxSelections) */
  maxSelections?: number
}

function formatFallbackSelectedLabel(value: string): string {
  const normalized = String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!normalized) return ''

  return normalized
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = "Select options...",
  className,
  disabled = false,
  maxSelections,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [dropdownPosition, setDropdownPosition] = React.useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null)
  const visibleSelected = selected.filter((selectedId) => String(selectedId ?? '').trim().length > 0)

  const updateDropdownPosition = React.useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportPadding = 8
    const gap = 8
    const desiredHeight = 260
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap - viewportPadding)
    const spaceAbove = Math.max(0, rect.top - gap - viewportPadding)
    const placeAbove = spaceBelow < 180 && spaceAbove > spaceBelow
    const maxHeight = Math.max(140, Math.min(desiredHeight, placeAbove ? spaceAbove : spaceBelow))
    const top = placeAbove
      ? Math.max(viewportPadding, rect.top - gap - maxHeight)
      : rect.bottom + gap

    setDropdownPosition({
      top,
      left: rect.left,
      width: rect.width,
      maxHeight,
    })
  }, [])

  const handleUnselect = React.useCallback((option: string) => {
    onChange(selected.filter((s) => s !== option))
  }, [onChange, selected])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current
    if (input) {
      if (e.key === "Delete" || e.key === "Backspace") {
        if (input.value === "" && visibleSelected.length > 0) {
          handleUnselect(visibleSelected[visibleSelected.length - 1])
        }
      }
      // This is not a default behaviour of the <input /> field
      if (e.key === "Escape") {
        input.blur()
      }
    }
  }, [handleUnselect, visibleSelected])

  const atMax = maxSelections != null && visibleSelected.length >= maxSelections
  const selectables = atMax
    ? []
    : options.filter((option) => !selected.includes(option.id))

  React.useEffect(() => {
    if (!open) return

    updateDropdownPosition()

    const handleWindowChange = () => {
      updateDropdownPosition()
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
      setInputValue("")
    }

    window.addEventListener("resize", handleWindowChange)
    window.addEventListener("scroll", handleWindowChange, true)
    document.addEventListener("mousedown", handleOutsideClick)

    return () => {
      window.removeEventListener("resize", handleWindowChange)
      window.removeEventListener("scroll", handleWindowChange, true)
      document.removeEventListener("mousedown", handleOutsideClick)
    }
  }, [open, updateDropdownPosition])

  return (
    <CommandPrimitive
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
      shouldFilter={true}
      filter={(value, search) => {
        // Always show items when dropdown is open, filter by search input
        if (search === "") return 1
        if (value.toLowerCase().includes(search.toLowerCase())) return 1
        return 0
      }}
    >
      <div
        ref={triggerRef}
        className={cn(
          "group rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-text",
          className
        )}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus()
            setOpen(true)
          }
        }}
      >
        <div className="flex flex-wrap gap-1 items-center min-h-[20px] w-full">
          {visibleSelected.length === 0 && !open && inputValue === '' ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : visibleSelected.length > 0 ? (
            <>
              {visibleSelected.map((selectedId) => {
                const option = options.find(opt => opt.id === selectedId)
                const label = option?.name?.trim() || formatFallbackSelectedLabel(selectedId)
                if (!label) return null
                return (
                  <Tag
                    key={selectedId}
                    variant="neutral"
                    size="sm"
                    maxWidth="100%"
                    className="shrink-0 border-[#20AEDC] bg-[#20AEDC] text-white"
                    onRemove={() => handleUnselect(selectedId)}
                    removeAriaLabel={`Remove ${label}`}
                  >
                    {label}
                  </Tag>
                )
              })}
            </>
          ) : null}
          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => {
              setOpen(false);
              setInputValue('');
            }}
            onFocus={() => setOpen(true)}
            placeholder=""
            disabled={disabled}
            className="flex-1 bg-transparent outline-none min-w-[1px]"
          />
        </div>
      </div>
      {open && dropdownPosition
        ? createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[9999] rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
            >
              <CommandPrimitive.List
                className="overflow-auto"
                style={{ maxHeight: dropdownPosition.maxHeight }}
              >
                <CommandPrimitive.Empty className="py-2 px-2 text-sm text-muted-foreground">
                  No results found.
                </CommandPrimitive.Empty>
                <CommandPrimitive.Group>
                  {selectables.map((option) => {
                    return (
                      <CommandPrimitive.Item
                        key={option.id}
                        value={option.name}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onSelect={() => {
                          setInputValue("")
                          if (maxSelections != null && selected.length >= maxSelections) return
                          onChange([...selected, option.id])
                        }}
                        className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled=true]:pointer-events-none data-[selected='true']:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50"
                      >
                        {option.name}
                      </CommandPrimitive.Item>
                    )
                  })}
                </CommandPrimitive.Group>
              </CommandPrimitive.List>
            </div>,
            document.body
          )
        : null}
    </CommandPrimitive>
  )
}
